import React, { useEffect, useState } from "react";
import SearchDropdown from "../applicationUi/components/SearchDropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  createAppointment,
  fetchAppointmentsByClinicianId,
  checkForConflicts,
  updateAppointment,
} from "../store/thunks/appointmentsThunk";
import { appendAppointmentToBlock } from "../store/thunks/blocksThunk";
import { fetchClinicianById } from "../store/thunks/cliniciansThunk";
import { fetchClinicResources } from "../store/thunks/resourceThunk";
import { checkResourceAvailability } from "../store/thunks/resourceScheduleThunk";
import QuickAddClientModal from "./QuickAddClientModal";
import { format } from "date-fns";
import { Dialog } from "@headlessui/react";
import {
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";

const QuickAddAppointmentModal = ({
  isOpen,
  onClose,
  initialDate,
  initialTime,
  initialClinicianFname,
  initialClinicianLname,
  selectedClinician: initialSelectedClinician,
  clinic,
  mode = "create",
  appointment = null,
  blockId = null,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const availableResources =
    useSelector((state) => state.resources.resources) || [];

  const [isNewClient, setIsNewClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [isBlock, setIsBlock] = useState(false);
  const [blockDates, setBlockDates] = useState([]);
  const conflicts = useSelector((state) => state.appointments.conflicts) || [];

  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceInterval, setRecurrenceInterval] = useState(7); // Default weekly
  const [recurringDates, setRecurringDates] = useState([]); // To store calculated recurring dates

  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [totalSessions, setTotalSessions] = useState(10); // Add this line

  const [selectedClinician, setSelectedClinician] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const [selectedResources, setSelectedResources] = useState([]);
  const [resourceTypeFilter, setResourceTypeFilter] = useState("");

  const [modalContent, setModalContent] = useState({
    date: initialDate || null,
    time: initialTime || "",
    clinicianFname: initialClinicianFname || "",
    clinicianLname: initialClinicianLname || "",
  });

  const [resourceAvailability, setResourceAvailability] = useState({});

  useEffect(() => {
    if (
      initialSelectedClinician?.defaultLocation ||
      selectedClinician?.defaultLocation
    ) {
      dispatch(
        fetchClinicResources(
          initialSelectedClinician?.defaultLocation ||
            selectedClinician?.defaultLocation
        )
      );
    }
  }, [dispatch, initialSelectedClinician, selectedClinician]);

  useEffect(() => {
    if (mode === "edit" && appointment) {
      const start = new Date(appointment.start);
      const hours = start.getHours().toString().padStart(2, "0");
      const minutes = start.getMinutes().toString().padStart(2, "0");

      setModalContent({
        date: start,
        time: `${hours}:${minutes}`,
        clinicianFname: appointment.clinician.firstName,
        clinicianLname: appointment.clinician.lastName,
      });

      setSelectedClient(appointment.client);
      setSelectedService({
        ...appointment.service,
        duration: appointment.service?.duration ?? 30,
      });
      setSelectedClinician(appointment.clinician);
    }
  }, [mode, appointment]);

  useEffect(() => {
    if (
      mode === "create" &&
      initialSelectedClinician &&
      initialSelectedClinician._id
    ) {
      dispatch(fetchClinicianById(initialSelectedClinician._id)).then(
        (response) => {
          setSelectedClinician(response.payload);
        }
      );
    } else if (mode === "edit" && appointment) {
      setSelectedClinician(appointment.clinician);
    }
  }, [initialSelectedClinician, mode, appointment, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (
      isBlock &&
      modalContent.date &&
      modalContent.time &&
      totalSessions > 0 &&
      selectedClinician
    ) {
      try {
        const [hours, minutes] = modalContent.time.split(":").map(Number);
        const dates = Array.from({ length: totalSessions }, (_, i) => {
          const date = new Date(modalContent.date);
          date.setDate(date.getDate() + i * 7); // Default weekly for blocks
          date.setHours(hours);
          date.setMinutes(minutes);
          return date;
        });

        setBlockDates(dates); // Save block dates for preview

        const clinicianId = selectedClinician._id;
        dispatch(checkForConflicts({ dates, clinicianId }))
          .then((action) =>
            console.log("Conflicts for blocks:", action.payload)
          )
          .catch((error) =>
            console.error("Failed to fetch block conflicts:", error)
          );
      } catch (error) {
        console.error("Error calculating block dates:", error);
      }
    } else {
      setBlockDates([]); // Clear block dates if conditions are not met
    }
  }, [
    isBlock,
    modalContent.date,
    modalContent.time,
    totalSessions,
    selectedClinician,
    dispatch,
  ]);

  useEffect(() => {
    if (
      isRecurring &&
      modalContent.date &&
      modalContent.time &&
      totalSessions > 0 &&
      selectedClinician
    ) {
      try {
        const [hours, minutes] = modalContent.time.split(":").map(Number);
        const dates = Array.from({ length: totalSessions }, (_, i) => {
          const date = new Date(modalContent.date);
          date.setDate(date.getDate() + i * recurrenceInterval); // Use recurrenceInterval for recurring
          date.setHours(hours);
          date.setMinutes(minutes);
          return date;
        });

        setRecurringDates(dates); // Save recurring dates for preview

        const clinicianId = selectedClinician._id;
        dispatch(checkForConflicts({ dates, clinicianId }))
          .then((action) =>
            console.log("Conflicts for recurring:", action.payload)
          )
          .catch((error) =>
            console.error("Failed to fetch recurring conflicts:", error)
          );
      } catch (error) {
        console.error("Error calculating recurring dates:", error);
      }
    } else {
      setRecurringDates([]); // Clear recurring dates if conditions are not met
    }
  }, [
    isRecurring,
    modalContent.date,
    modalContent.time,
    totalSessions,
    recurrenceInterval,
    selectedClinician,
    dispatch,
  ]);

  useEffect(() => {
    // Log blockId for debugging
    if (blockId) {
      console.log("QuickAddAppointmentModal received blockId:", blockId);
    }
  }, [blockId]);

  useEffect(() => {
    console.log("Fetch available resource useEffect");

    console.log("modalContent", modalContent);
    console.log("selectedService", selectedService);
    console.log("selectedClinician", selectedClinician);
    console.log("selectedRoom", selectedRoom);
    console.log("availableResources", availableResources);
    console.log("selectedClient", selectedClient);

    if (
      modalContent.date &&
      modalContent.time &&
      selectedService?.duration &&
      selectedClinician?.defaultLocation
    ) {
      console.log("Fetching available resources");
      const [hours, minutes] = modalContent.time.split(":").map(Number);
      const start = new Date(modalContent.date);
      start.setHours(hours);
      start.setMinutes(minutes);

      dispatch(
        checkResourceAvailability({
          clinicId: selectedClinician.defaultLocation,
          start: start.toISOString(),
          duration: selectedService.duration,
        })
      ).then((action) => {
        // Store the availability response in a more easily accessible format
        const availabilityMap = {};
        action.payload.forEach((resource) => {
          availabilityMap[resource.resourceId] = {
            bookingStatus: resource.bookingStatus,
            overlappingBookings: resource.overlappingBookings,
          };
        });
        setResourceAvailability(availabilityMap);
        console.log("resourceAvailability", availabilityMap);
        console.log("availableResources", resourceAvailability);
      });
    }
  }, [
    modalContent.date,
    modalContent.time,
    selectedService,
    selectedClinician,
    appointment,
    dispatch,
  ]);

  const handleServiceSelection = (service) => {
    setSelectedService(service);
  };

  const handleReferralSelection = (referral) => {
    setSelectedReferral(referral);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setModalContent((prevContent) => ({
      ...prevContent,
      [name]: value,
    }));
  };

  // Handle date changes from the date input
  const handleDateChange = (e) => {
    const newDateStr = e.target.value; // "YYYY-MM-DD"
    if (newDateStr) {
      const [year, month, day] = newDateStr.split("-");
      const updatedDate = new Date(modalContent.date || Date.now());
      updatedDate.setFullYear(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      );
      setModalContent((prev) => ({ ...prev, date: updatedDate }));
    }
  };

  const handleAppendToBlock = (appointmentData) => {
    dispatch(
      appendAppointmentToBlock({
        blockId,
        appointmentData,
      })
    )
      .unwrap()
      .then(() => {
        onClose();
        alert("Appointment successfully added to block!");
      })
      .catch((err) => {
        console.error("Failed to append appointment to block:", err);
        alert("Failed to append appointment. Check console for details.");
      });
  };

  const handleSubmit = (event) => {
    if (isRecurring) {
      handleRecurringSave(event);
    } else {
      handleSave(event);
    }
  };

  const handleSave = (event) => {
    event.preventDefault();
    const { date, time } = modalContent;

    if (!time) {
      console.error("Time is not defined");
      return;
    }

    if (
      !selectedClient ||
      !selectedService ||
      !selectedClinician ||
      !selectedClinician.defaultLocation
    ) {
      alert("All fields must be nonempty");
      return;
    }

    const [hours, minutes] = time.split(":").map(Number);
    const start = new Date(date);
    start.setHours(hours, minutes, 0, 0);
    const end = new Date(start);
    end.setMinutes(start.getMinutes() + (selectedService.duration || 30));

    const appointmentData = {
      clinic: selectedClinician.defaultLocation,
      clinician: selectedClinician._id,
      client: selectedClient._id,
      title: selectedService.description,
      start: start.toISOString(),
      end: end.toISOString(),
      service: selectedService._id,
      status: mode === "create" ? ["Booked"] : appointment?.status,
      history:
        mode === "create"
          ? [
              {
                status: "Booked",
                comments: "",
                timestamp: new Date().toISOString(),
                user: user._id,
              },
            ]
          : [
              ...appointment.history,
              {
                status: "Changed",
                comments: "Appointment changed",
                user: user._id,
                timestamp: new Date().toISOString(),
              },
            ],
      userId: user._id,
    };

    // Only include resource fields in create mode
    if (mode === "create") {
      appointmentData.resource = selectedRoom?._id;
      appointmentData.resources = selectedResources.map((r) => r._id);
    }

    // Add isBlock and totalSessions if mode is create and isBlock is true
    if (mode === "create" && isBlock) {
      appointmentData.isBlock = true;
      appointmentData.totalSessions = totalSessions;
    }

    if (blockId) {
      // Append appointment to block if blockId is provided
      dispatch(appendAppointmentToBlock({ blockId, appointmentData }))
        .unwrap()
        .then(() => {
          alert("Appointment successfully added to block!");
          onClose();
        })
        .catch((err) => {
          console.error("Failed to append appointment to block:", err);
          alert("Failed to append appointment to block.");
        });
    } else if (mode === "create") {
      // Create new appointment or block
      dispatch(createAppointment(appointmentData))
        .unwrap()
        .then(() => {
          alert("Appointment successfully created!");
          onClose();
        })
        .catch((err) => {
          console.error("Failed to create appointment:", err);
          alert("Failed to create appointment.");
        });
    } else if (mode === "edit" && appointment) {
      // Update existing appointment logic
      dispatch(
        updateAppointment({ id: appointment._id, updatedData: appointmentData })
      )
        .unwrap()
        .then(() => {
          alert("Appointment successfully updated!");
          onClose();
        })
        .catch((err) => {
          console.error("Failed to update appointment:", err);
          alert("Failed to update appointment.");
        });
    }
  };

  const handleRecurringSave = (event) => {
    event.preventDefault();
    const { date, time } = modalContent;

    if (!time) {
      console.error("Time is not defined");
      return;
    }

    if (
      !selectedClient ||
      !selectedService ||
      !selectedClinician ||
      !selectedClinician.defaultLocation
    ) {
      alert("All fields must be nonempty");
      return;
    }

    const [hours, minutes] = time.split(":").map(Number);
    const baseStartDate = new Date(date);
    baseStartDate.setHours(hours, minutes, 0, 0);

    const baseEndDate = new Date(baseStartDate);
    baseEndDate.setMinutes(
      baseStartDate.getMinutes() + (selectedService.duration || 30)
    );

    // Compute recurring dates
    const interval = isRecurring ? recurrenceInterval : 7; // Default to weekly if no interval
    const recurringDates = [];
    for (let i = 0; i < totalSessions; i++) {
      const start = new Date(baseStartDate);
      start.setDate(start.getDate() + i * interval);

      const end = new Date(start);
      end.setMinutes(start.getMinutes() + (selectedService.duration || 30));

      recurringDates.push({ start, end });
    }

    // Dispatch the `createAppointment` thunk for each recurring date
    recurringDates.forEach(({ start, end }) => {
      const updatedData = {
        clinic: selectedClinician.defaultLocation,
        clinician: selectedClinician._id,
        client: selectedClient._id,
        title: selectedService.description,
        start: start.toISOString(),
        end: end.toISOString(),
        service: selectedService._id,
        status: ["Booked"],
        history: [
          {
            status: "Booked",
            comments: "Booked as recurring appointment",
            timestamp: new Date().toISOString(),
            user: user._id,
          },
        ],
        userId: user._id,
      };

      dispatch(createAppointment(updatedData));
    });

    onClose();
  };

  const handleResourceSelection = (resource) => {
    const availability = resourceAvailability[resource._id];
    if (!availability || availability.bookingStatus !== "Available") return;

    // Check if already selected
    const isAlreadySelected = selectedResources.some(
      (r) => r._id === resource._id
    );

    if (isAlreadySelected) {
      // Remove from selected resources
      setSelectedResources((prev) =>
        prev.filter((r) => r._id !== resource._id)
      );
    } else {
      // Add to selected resources
      setSelectedResources((prev) => [...prev, resource]);
    }
  };

  if (!isOpen) return null;

  // Convert date to YYYY-MM-DD for input
  const dateInputValue =
    modalContent.date instanceof Date && !isNaN(modalContent.date)
      ? modalContent.date.toISOString().slice(0, 10)
      : "";

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-60 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-gray-900/50 transition-opacity" />

        {/* Centering trick */}
        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-sm">
          <div className="absolute right-4 top-4">
            <button
              type="button"
              className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={onClose}>
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="bg-white px-6 py-6">
            <form onSubmit={handleSubmit}>
              {/* Title */}
              <Dialog.Title
                as="h3"
                className="text-xl font-semibold leading-6 text-gray-900 mb-6">
                {mode === "edit"
                  ? "Update Appointment"
                  : "Create New Appointment"}
              </Dialog.Title>

              <div className="space-y-6">
                {/* Client Information Section */}
                <div className="space-y-6 border-b border-gray-900/10 pb-6">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <UserIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Client Information
                    </h3>
                  </div>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    {mode === "edit"
                      ? "Search for a different client or keep the existing client selected."
                      : "Search for an existing client or create a new one."}
                  </p>

                  {selectedClient && selectedClient.phoneNumbers && (
                    <div className="bg-gray-50 p-4 rounded-sm">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Selected Client
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedClient.firstName} {selectedClient.lastName},{" "}
                        {selectedClient.email},{" "}
                        {selectedClient.phoneNumbers
                          .map(
                            (phone) =>
                              `${phone.type}: ${phone.number} ${phone.note}`
                          )
                          .join(", ")}
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <SearchDropdown
                      prefix=":client:"
                      displayFields={[
                        "firstName",
                        "lastName",
                        "email",
                        "phoneNumbers",
                      ]}
                      onSelection={(client) => setSelectedClient(client)}
                      placeholder="Search for a client"
                      value={selectedClient}
                      className="w-full"
                    />
                    {mode === "create" && (
                      <button
                        type="button"
                        onClick={() => setIsNewClient(true)}
                        className="rounded-sm bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                        Add a New Client
                      </button>
                    )}
                  </div>
                </div>

                {/* Appointment Information Section */}
                <div className="space-y-6 border-b border-gray-900/10 pb-6">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <CalendarIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Appointment Information
                    </h3>
                  </div>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    {mode === "edit"
                      ? "You can now change the date, time, clinician, and service."
                      : "Date, Time, and Clinician are prepopulated from your selection."}
                  </p>

                  {/* Date and Time */}
                  <div className="sm:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-1.5">
                        <CalendarIcon className="h-5 w-5 text-gray-500" />
                        <label className="text-sm font-medium text-gray-900">
                          Date
                        </label>
                      </div>
                      <input
                        type="date"
                        value={dateInputValue}
                        onChange={handleDateChange}
                        className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1.5">
                        <ClockIcon className="h-5 w-5 text-gray-500" />
                        <label className="text-sm font-medium text-gray-900">
                          Time
                        </label>
                      </div>
                      <input
                        type="time"
                        name="time"
                        value={modalContent.time || ""}
                        onChange={handleInputChange}
                        className="block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Clinician */}
                  <div>
                    <div className="flex items-center space-x-2 mb-1.5">
                      <UserIcon className="h-5 w-5 text-gray-500" />
                      <label className="text-sm font-medium text-gray-900">
                        Clinician
                      </label>
                    </div>
                    {selectedClinician && selectedClinician.specialization && (
                      <div className="bg-gray-50 p-3 rounded-sm mb-2">
                        <p className="text-sm text-gray-600">
                          {selectedClinician.firstName}{" "}
                          {selectedClinician.lastName}
                          {selectedClinician.specialization.length > 0 && (
                            <>, {selectedClinician.specialization.join(", ")}</>
                          )}
                        </p>
                      </div>
                    )}
                    <SearchDropdown
                      prefix=":user:"
                      displayFields={["firstName", "lastName", "email"]}
                      onSelection={(clinician) => {
                        setSelectedClinician(clinician);
                        setModalContent((prev) => ({
                          ...prev,
                          clinicianFname: clinician.firstName,
                          clinicianLname: clinician.lastName,
                        }));
                      }}
                      placeholder="Search for a clinician"
                      className="w-full"
                    />
                  </div>

                  {/* Service and Duration */}
                  <div>
                    <div className="flex items-center space-x-2 mb-1.5">
                      <ClockIcon className="h-5 w-5 text-gray-500" />
                      <label className="text-sm font-medium text-gray-900">
                        Service
                      </label>
                    </div>
                    {selectedService && (
                      <div className="bg-gray-50 p-3 rounded-sm mb-2">
                        <p className="text-sm text-gray-600">
                          {selectedService.description}, $
                          {typeof selectedService.price === "number"
                            ? selectedService.price.toFixed(2)
                            : "N/A"}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-4">
                      <div className="flex-1 w-full">
                        <SearchDropdown
                          prefix=":service:"
                          displayFields={[
                            "serviceCode",
                            "description",
                            "price",
                          ]}
                          onSelection={handleServiceSelection}
                          placeholder="Search for a service by name or service code"
                          activeServices={showActiveOnly}
                          className="w-full"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          id="showActiveOnly"
                          type="checkbox"
                          checked={showActiveOnly}
                          onChange={() => setShowActiveOnly(!showActiveOnly)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="showActiveOnly"
                          className="ml-2 text-sm text-gray-900">
                          Show only active services
                        </label>
                      </div>
                    </div>

                    {selectedService && (
                      <div className="mt-4">
                        <label className="text-sm font-medium text-gray-900">
                          Duration
                        </label>
                        <select
                          value={selectedService?.duration ?? 30}
                          onChange={(e) =>
                            setSelectedService({
                              ...selectedService,
                              duration: parseInt(e.target.value),
                            })
                          }
                          disabled={!selectedService}
                          className="mt-1 block w-full rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
                          {[
                            15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165,
                            180, 195, 210, 225, 240,
                          ].map((minutes) => (
                            <option key={minutes} value={minutes}>
                              {Math.floor(minutes / 60)}h {minutes % 60}m
                            </option>
                          ))}
                        </select>
                        {selectedService.duration === 0 && (
                          <p className="mt-2 text-sm text-red-600">
                            Please select a duration for this service.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Resources Section - Only show in create mode */}
                {mode !== "edit" && (
                  <div className="space-y-6 border-b border-gray-900/10 pb-6">
                    <div className="flex items-center space-x-2 mb-1.5">
                      <HomeIcon className="h-5 w-5 text-gray-500" />
                      <h3 className="text-base font-semibold text-gray-900">
                        Resources
                      </h3>
                    </div>
                    {modalContent.time && selectedService ? (
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <p className="text-sm text-gray-600">
                            Select available resources for this appointment
                          </p>
                          <select
                            value={resourceTypeFilter}
                            onChange={(e) =>
                              setResourceTypeFilter(e.target.value)
                            }
                            className="rounded-sm border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm">
                            <option value="">All Resources</option>
                            <option value="Room">Rooms</option>
                            <option value="Equipment">Equipment</option>
                            <option value="Testing Material">
                              Testing Materials
                            </option>
                            <option value="Assistive Device">
                              Assistive Devices
                            </option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="space-y-4">
                          {Object.entries(
                            availableResources
                              .filter((resource) =>
                                resourceTypeFilter
                                  ? resource.type === resourceTypeFilter &&
                                    resource.status === "active"
                                  : resource.status === "active"
                              )
                              .reduce((acc, resource) => {
                                if (!acc[resource.type])
                                  acc[resource.type] = [];
                                acc[resource.type].push(resource);
                                return acc;
                              }, {})
                          ).map(([type, resources]) => (
                            <div
                              key={type}
                              className="bg-gray-50 p-3 rounded-sm">
                              <h4 className="text-sm font-medium text-gray-900 mb-2">
                                {type}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {resources.map((resource) => {
                                  const availability =
                                    resourceAvailability[resource._id];
                                  const overlappingBookings =
                                    availability?.overlappingBookings || [];
                                  const bookingTimes =
                                    overlappingBookings.length > 0
                                      ? overlappingBookings
                                          .map(
                                            (booking) =>
                                              `${format(
                                                new Date(booking.start),
                                                "h:mm a"
                                              )} - ${format(
                                                new Date(booking.end),
                                                "h:mm a"
                                              )}`
                                          )
                                          .join(", ")
                                      : null;

                                  return (
                                    <div
                                      key={resource._id}
                                      className="py-2 flex items-center">
                                      <input
                                        id={`resource-${resource._id}`}
                                        name={`resource-${resource._id}`}
                                        type="checkbox"
                                        checked={selectedResources.some(
                                          (r) => r._id === resource._id
                                        )}
                                        onChange={() =>
                                          handleResourceSelection(resource)
                                        }
                                        disabled={
                                          !availability ||
                                          availability.bookingStatus !==
                                            "Available"
                                        }
                                        className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ${
                                          !availability ||
                                          availability.bookingStatus !==
                                            "Available"
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                        }`}
                                      />
                                      <label
                                        htmlFor={`resource-${resource._id}`}
                                        className="ml-3 block text-sm leading-6 text-gray-900">
                                        {resource.name}
                                        {resource.description &&
                                          ` - ${resource.description}`}
                                        {resource.location &&
                                          ` (${resource.location})`}
                                        <span
                                          className={`ml-2 ${
                                            !availability
                                              ? "text-gray-400"
                                              : availability.bookingStatus ===
                                                "Available"
                                              ? "text-green-600"
                                              : availability.bookingStatus ===
                                                "Unavailable"
                                              ? "text-orange-600"
                                              : "text-red-600"
                                          }`}>
                                          (
                                          {availability
                                            ? availability.bookingStatus
                                            : "Loading..."}
                                          )
                                        </span>
                                        {availability?.bookingStatus ===
                                          "Unavailable" &&
                                          bookingTimes && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              Unavailable: {bookingTimes}
                                            </div>
                                          )}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        Please select an appointment time and service first to
                        view available resources.
                      </p>
                    )}
                  </div>
                )}

                {/* Booking Options Section */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-2 mb-1.5">
                    <CalendarIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-base font-semibold text-gray-900">
                      Booking Options
                    </h3>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center">
                      <input
                        id="isBlock"
                        name="isBlock"
                        type="checkbox"
                        checked={isBlock}
                        onChange={() => {
                          setIsBlock(!isBlock);
                          if (!isBlock) setIsRecurring(false);
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <label
                        htmlFor="isBlock"
                        className="ml-2 text-sm text-gray-900">
                        Block Appointment
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="isRecurring"
                        name="isRecurring"
                        type="checkbox"
                        checked={isRecurring}
                        onChange={() => {
                          setIsRecurring(!isRecurring);
                          if (!isRecurring) setIsBlock(false);
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <label
                        htmlFor="isRecurring"
                        className="ml-2 text-sm text-gray-900">
                        Recurring Appointment
                      </label>
                    </div>
                    <div className="flex items-center">
                      <label className="text-sm font-medium text-gray-900 mr-2">
                        Total Sessions:
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={totalSessions}
                        onChange={(e) =>
                          setTotalSessions(parseInt(e.target.value, 10))
                        }
                        className="block w-20 rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                    {isRecurring && recurringDates.length > 0 && (
                      <div className="flex items-center">
                        <label className="text-sm font-medium text-gray-900 mr-2">
                          Repeat every
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={recurrenceInterval}
                          onChange={(e) =>
                            setRecurrenceInterval(parseInt(e.target.value, 10))
                          }
                          className="block w-20 rounded-sm border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                        <span className="ml-2 text-sm text-gray-900">days</span>
                      </div>
                    )}
                  </div>

                  {/* Block Grid */}
                  {isBlock && blockDates.length > 0 && (
                    <div className="mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {blockDates.map((date, index) => (
                          <div
                            key={index}
                            className="p-4 border rounded-sm bg-gray-50">
                            <p className="text-sm font-medium text-gray-900">
                              Block #{index + 1}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(date).toDateString()} at{" "}
                              {new Date(date).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </p>
                            {index === 6 && (
                              <p className="text-xs text-gray-600 mt-1">
                                (Progress Report)
                              </p>
                            )}
                            {conflicts[index] && (
                              <p className="mt-2 text-sm">
                                <span
                                  className={`font-semibold ${
                                    conflicts[index].status === "No conflict"
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}>
                                  {conflicts[index].status}
                                </span>
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const totalConflicts = conflicts.reduce(
                          (sum, conflict) =>
                            sum +
                            conflict.conflictDetails.appointments +
                            conflict.conflictDetails.meetings,
                          0
                        );
                        return totalConflicts > 0 ? (
                          <p className="mt-4 text-sm text-red-600 text-center">
                            {totalConflicts} conflicting appointments/meetings
                            detected. Booking is still permitted.
                          </p>
                        ) : (
                          <p className="mt-4 text-sm text-green-600 text-center">
                            No conflicts detected. Booking is permitted.
                          </p>
                        );
                      })()}
                    </div>
                  )}

                  {/* Recurring Grid */}
                  {isRecurring && recurringDates.length > 0 && (
                    <div className="mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {recurringDates.map((date, index) => (
                          <div
                            key={index}
                            className="p-4 border rounded-sm bg-gray-50">
                            <p className="text-sm font-medium text-gray-900">
                              Appointment #{index + 1}
                            </p>
                            <p className="text-sm text-gray-600">
                              {new Date(date).toDateString()} at{" "}
                              {new Date(date).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </p>
                            {conflicts[index] && (
                              <p className="mt-2 text-sm">
                                <span
                                  className={`font-semibold ${
                                    conflicts[index].status === "No conflict"
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}>
                                  {conflicts[index].status}
                                </span>
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const totalConflicts = conflicts.reduce(
                          (sum, conflict) =>
                            sum +
                            conflict.conflictDetails.appointments +
                            conflict.conflictDetails.meetings,
                          0
                        );
                        return totalConflicts > 0 ? (
                          <p className="mt-4 text-sm text-red-600 text-center">
                            {totalConflicts} conflicting appointments/meetings
                            detected. Booking is still permitted.
                          </p>
                        ) : (
                          <p className="mt-4 text-sm text-green-600 text-center">
                            No conflicts detected. Booking is permitted.
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="mt-6 flex items-center justify-end gap-x-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm font-semibold leading-6 text-gray-900">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-sm bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  {mode === "edit"
                    ? "Confirm Update Appointment"
                    : isRecurring
                    ? `Create ${recurringDates.length} Recurring Appointment${
                        recurringDates.length > 1 ? "s" : ""
                      }`
                    : isBlock
                    ? `Create ${blockDates.length} Block Appointment${
                        blockDates.length > 1 ? "s" : ""
                      }`
                    : "Create Appointment"}
                </button>
              </div>
            </form>

            {isNewClient && (
              <QuickAddClientModal onClose={() => setIsNewClient(false)} />
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default QuickAddAppointmentModal;
